<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.vehicle_add) || checkPermission(layoutPermissions.vehicle_administration)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddVehicle">
                {{ $t('labels.addVehicle') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="dataTables"
            prefix-module="vehicle"
            :headers="headerInternal"
            :server="apiListVehicles"
            :query-extend="{ type: 0 }"
            :use-archive="apiArchive"
            :use-restore="apiRestore"
            use-selection
            store-name="vehicleDataTable"
            store-key="Vehicle" />
        <document
            ref="showDrawerDocumentVehicle"
            type="vehicle"
            :reload="isReload" />
        <add-vehicle
            v-if="checkPermission(layoutPermissions.vehicle_add) || checkPermission(layoutPermissions.vehicle_administration)"
            ref="showDrawerAddVehicle"
            :column="2"
            @refresh-data-tables="refreshDataTables()" />
        <list-plugin
            v-if="checkPermission(layoutPermissions.vehicle_plugin_management)"
            ref="showDrawerListVehiclePlugin"
            :reload="isReload" />
        <edit-vehicle
            v-if="checkPermission(layoutPermissions.vehicle_edit) || checkPermission(layoutPermissions.vehicle_administration)"
            ref="showDrawerEditVehicle"
            :column="2"
            @refresh-data-tables="refreshDataTables()"
            @close="handleCloseFromEdit" />
        <hts-modal
            :show="showPopupDeleteVehicle"
            :title="$t('labels.deleteVehicle')"
            type="confirm"
            :content="$t('labels.confirmDeleteVehicle')"
            @close="closePopupDeleteVehicle"
            @confirm="() => actionDeleteVehicle()" />
        <detail-vehicle
            ref="showDrawerListVehicleDetail"
            :vehicle-id="vehicleId"
            :column="2"
            :list-type-vehicles="listTypeVehicles"
            @refresh-data-tables="refreshDataTables()" />
        <hts-modal
            :show="showPopupChangeStatusVehicle"
            :title="$t('labels.statusChanges')"
            type="confirm"
            :content="$t('labels.statusChangesContent')"
            @hide="showPopupChangeStatusVehicle = false"
            @confirm="() => actionChangesStatusVehicle()" />
        <stop-exploit
            ref="stopExploit"
            @refreshTables="refreshDataTables()" />
        <open-exploit
            ref="openExploit"
            @refreshTables="refreshDataTables()" />
    </div>
</template>

<script lang="ts" setup>
import Actions from './ignore/actions.vue'
import AddVehicle from './ignore/add-vehicle.vue'
import DetailVehicle from './ignore/detail-vehicle.vue'
import Document from './ignore/document/document.vue'
import EditVehicle from './ignore/edit-vehicle.vue'
import OpenExploit from './ignore/exploit/open.vue'
import StopExploit from './ignore/exploit/stop.vue'
import { IVehicle } from './ignore/imports/form'
import ListPlugin from './ignore/plugin/list-plugin.vue'
import Status from './ignore/status.vue'
import StatusComponent from '~/components/status.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { $api } from '#imports'

const route = useRoute()

const { $formatters } = useNuxtApp()

const source = ref(0)

const apiArchive = computed(() => {
  return { api: $url.vehicle.vehicle.archive, permissions: checkPermission(layoutPermissions.vehicle_archive) }
})

const apiRestore = computed(() => {
  return { api: $url.vehicle.vehicle.restore, permissions: checkPermission(layoutPermissions.vehicle_restore) }
})

watch(
  () => source?.value,
  () => {
    if (dataTables.value) {
      dataTables.value.refresh()
    }
  }
)

interface ITypeVehicle {
  code: string
  name: string
}

const listTypeVehicles = ref<ITypeVehicle[]>([])
const showDrawerAddVehicle: any = ref()
const showDrawerEditVehicle: any = ref()
const showDrawerDocumentVehicle: any = ref()
const showDrawerAddDocumentVehicle: any = ref()
const showDrawerListVehiclePlugin: any = ref()
const showDrawerListVehicleDetail: any = ref()
const vehicleId = ref()
const dataTables = ref()
const checkDisabledEdit = ref(true)
const showPopupDeleteVehicle = ref(false)
const vehicleIsSelectedToDelete = ref('')
const isReload = ref(false)
const dataChangeStatus = ref()
const showPopupChangeStatusVehicle = ref(false)
const valueStatus = ref()
const stopExploit = ref()
const openExploit = ref()

const apiListVehicles = $url.vehicle.vehicle['get-list']

const handleViewDetail = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  vehicleId.value = vehicle._id
  if (showDrawerListVehicleDetail.value) {
    showDrawerListVehicleDetail.value.openEditVehicle(vehicle._id)
  }
}
const handleViewDocument = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  vehicleId.value = vehicle._id
  if (showDrawerDocumentVehicle.value) {
    showDrawerDocumentVehicle.value.openDrawerDocument(vehicle._id)
  }
}

const handleAddDocument = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  vehicleId.value = vehicle._id
  if (showDrawerAddDocumentVehicle.value) {
    showDrawerAddDocumentVehicle.value.openAddDocument()
  }
}
const handleEditVehicle = (vehicle: IVehicle) => {
  if (!vehicle) {
    return null
  }
  const id: string = route.query.id ? String(route.query.id) : ''
  if (id && vehicle._id && vehicle._id !== id) {
    checkDisabledEdit.value = true
  }
  if (id && vehicle._id && vehicle._id === id) {
    checkDisabledEdit.value = false
  }
  showDrawerEditVehicle.value.openEditVehicle(vehicle)
}
const handleDeleteVehicle = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  showPopupDeleteVehicle.value = true
  vehicleIsSelectedToDelete.value = vehicle._id
}

const handleViewPlugin = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  if (showDrawerListVehiclePlugin.value) {
    showDrawerListVehiclePlugin.value.openViewPlugin(vehicle)
  }
}

const checkOpenExploit = (data: any) => {
  const exploit = data.exploit

  return exploit
}

const checkStopExploit = (data: any) => {
  const exploit = data.exploit

  return !exploit
}

const handleCloseExploit = (data: any) => {
  if (stopExploit.value) {
    stopExploit.value.open(data._id)
  }
}

const handleOpenExploit = (data: any) => {
  if (stopExploit.value) {
    openExploit.value.open(data._id)
  }
}

const headerInternal = ref([
  {
    field: 'licensePlate',
    headerName: $t('labels.vehicleModule.licensePlate'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'exploit',
    headerName: $t('labels.exploitStatus'),
    cellRenderer: StatusComponent,
    cellRendererParams: {
      module: 'vehicleExploit',
      label: '',
      keyStatus: 'exploit'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: $t('labels.exploiting'), value: true, class: { '!bg-green-100': true, '!text-green-800': true } }, // Exploiting: Màu xanh lá cây
      { name: $t('labels.stopExploit'), value: false, class: { '!bg-red-100': true, '!text-red-800': true } } // Stop Exploit: Màu đỏ
    ],
    filterModelDefault: { type: 'equals', filterType: 'boolean' }
  },
  {
    field: 'status',
    headerName: $t('labels.vehiceReadyStatus'),
    cellRenderer: Status,
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: $t('labels.readyShip'), value: 1, class: { '!bg-green-100': true, '!text-green-600': true } }, // Ready to Ship: Màu xanh lá cây
      { name: $t('labels.vehicleModule.notReady'), value: -1, class: { '!bg-yellow-100': true, '!text-yellow-600': true } } // Not Ready: Màu vàng
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    field: 'approvedByManagement',
    headerName: $t('labels.administrationStatus'),
    cellRenderer: StatusComponent,
    cellRendererParams: {
      module: 'vehicleManagementApprove',
      label: '',
      keyStatus: 'approvedByManagement'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: $t('labels.approved'), value: true, class: { '!bg-green-100': true, '!text-green-800': true } }, // Approved: Màu xanh lá cây
      { name: $t('labels.waittingApprove'), value: false, class: { '!bg-yellow-100': true, '!text-yellow-800': true } } // Waiting for Approval: Màu vàng
    ],
    filterModelDefault: { type: 'equals', filterType: 'boolean' }
  },
  {
    field: 'documentDetail', // type // expiredDate
    headerName: 'Hạn đăng kiểm',
    valueGetter: (params: any) => {
      const expiredDate = (params.data.documentDetail || []).filter((item: any) => item.type === 'REGISTRY').map((item: any) => item.expiredDate)
      return expiredDate.length ? $formatters.date($lodash.max(expiredDate)) : ''
    }
  },
  {
    field: 'documentDetail2',
    headerName: 'Hạn bảo hiểm',
    valueGetter: (params: any) => {
      const expiredDate = (params.data.documentDetail || []).filter((item: any) => item.type === 'INSURANCE').map((item: any) => item.expiredDate)
      return expiredDate.length ? $formatters.date($lodash.max(expiredDate)) : ''
    }
  },
  {
    field: 'expiredYear',
    headerName: $t('labels.vehicleModule.expiredYear'),
    filter: 'agNumberColumnFilter',
    sortable: true
  },
  {
    field: 'vehiclePlugin',
    headerName: 'Phụ kiện',
    valueGetter: (params: any) => {
      const { vehiclePlugin } = params.data
      if (!vehiclePlugin || !Array.isArray(vehiclePlugin) || !vehiclePlugin.length) {
        return null
      }

      return vehiclePlugin.map((item) => item.name).join(', ')
    }
  },
  {
    field: 'parkingNodeName',
    headerName: $t('labels.parkingLot'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'categoryName',
    headerName: $t('labels.vehicleModule.type'),
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.vehicleLoad)) {
        return null
      }
      return getNameTypeVehicle(params.data.vehicleLoad)
    }
  },
  {
    field: 'isGenset',
    label: 'isGenset',
    headerName: $t('labels.vehicleModule.useGenset'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.isGenset)) {
        return null
      }
      if (params.data.isGenset) {
        return $t('labels.true')
      } else {
        return $t('labels.false')
      }
    }
  },
  {
    field: 'containerType',
    label: 'containerType',
    headerName: $t('labels.vehicleModule.containerType'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.containerType)) {
        return ''
      }
      if (params.data.containerType) {
        return $t('labels.vehicleModule.stickyContainer')
      } else {
        return $t('labels.vehicleModule.separatedContainer')
      }
    }
  },
  {
    field: 'price',
    headerName: $t('labels.vehicleModule.price'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.price)) {
        return null
      }
      return $formatters.formatCurrency(params.data.price)
    }
  },
  {
    field: 'driverName',
    headerName: $t('labels.driver'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'coDriverName',
    headerName: $t('labels.coDriver'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'weight',
    headerName: $t('labels.vehicleModule.weight'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.weight)) {
        return null
      }
      return $formatters.formatCurrency(params.data.weight)
    }
  },
  {
    field: 'manufacture',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.vehicleModule.manufacture')
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'vehicleDataTable',
      storeName: 'Vehicle',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          click: handleViewDetail,
          permissions: checkPermission(layoutPermissions.vehicle_management),
          isAll: true
        },
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_edit) || checkPermission(layoutPermissions.vehicle_administration),
          click: handleEditVehicle
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDocument'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_document_management),
          click: handleViewDocument
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewPlugin'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_plugin_management),
          click: handleViewPlugin
        },
        {
          icon: 'hi-lock-closed-icon',
          label: $t('labels.stopExploit'),
          class: 'text-blue-500 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_edit),
          isAll: true,
          validate: checkOpenExploit,
          click: handleCloseExploit
        },
        {
          icon: 'hi-lock-open-icon',
          label: $t('labels.openExploit'),
          class: 'text-blue-500 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_edit),
          isAll: true,
          validate: checkStopExploit,
          click: handleOpenExploit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.vehicle_delete) || checkPermission(layoutPermissions.vehicle_administration),
          click: handleDeleteVehicle,
          isArchive: true
        }
      ]
    }
  }
])

const headerOutsource = [
  {
    field: 'fleetVehicleDetail.name',
    headerName: $t('labels.nameFleetVehicle'),
    sortable: true
  },
  {
    field: 'licensePlate',
    headerName: $t('labels.vehicleModule.licensePlate'),
    sortable: true
  },
  {
    field: 'otherCompanyDriver.fullName',
    headerName: $t('labels.driverFullname')
  },
  {
    field: 'otherCompanyDriver.phone',
    headerName: $t('labels.driverPhoneNumber'),
    valueFormatter: (params: any) => {
      const code = params?.data?.otherCompanyDriver.phone.code || ''
      const phone = params?.data?.otherCompanyDriver.phone.phone || ''
      return code ? `(${code}) ${phone}` : phone
    }
  },
  {
    field: 'otherCompanyDriver.idCardNumber',
    headerName: $t('labels.driverIdCardNumber')
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'vehicleDataTable',
      storeName: 'Vehicle',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          click: handleViewDetail,
          isAll: true
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDocument'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_document_management),
          click: handleViewDocument
        },
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_edit) || checkPermission(layoutPermissions.vehicle_administration),
          click: handleEditVehicle
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewPlugin'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_plugin_management),
          click: handleViewPlugin
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: true,
          click: handleDeleteVehicle,
          isArchive: true
        }
      ]
    }
  }
]

const closePopupDeleteVehicle = () => {
  showPopupDeleteVehicle.value = false
  Object.assign(vehicleIsSelectedToDelete, {})
}

const actionDeleteVehicle = () => {
  const api = $url.vehicle.vehicle.delete
  const vehicle = vehicleIsSelectedToDelete.value
  $api(api, { body: { ids: [vehicle] } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      dataTables.value.refresh()
      showPopupDeleteVehicle.value = false
      process.client && $toast().success(resData.data)
    }
  })
}

const refreshDataTables = async () => {
  await getDataCategoryVehicle()
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const getInfoVehicleEdit = (vehicleId: string) => {
  const apiVehicle = $url.vehicle.vehicle.getById
  $api(apiVehicle, { uriParams: { ID: vehicleId } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      if (showDrawerEditVehicle.value) {
        showDrawerEditVehicle.value.openEditVehicle({ ...resData.data } as IVehicle)
      }
    }
  })
}
const handleAddVehicle = () => {
  if (showDrawerAddVehicle.value) {
    showDrawerAddVehicle.value.open(source.value)
  }
}
const actionChangesStatusVehicle = () => {
  const api = $url.vehicle.vehicle.changeStatus
  const data = {
    id: dataChangeStatus.value.data._id,
    status: valueStatus.value
  }
  $api(api, { body: data }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      process.client && $toast().success($t('labels.updateSuccess'))
      refreshDataTables()
      showPopupChangeStatusVehicle.value = false
    }
  })
}

const dataCategory = ref<Array<{ [key: string]: any }>>([])
const getDataCategoryVehicle = async () => {
  const api = $urlAdmin.vehicle.category.getAllList
  try {
    const res = await $api(api)
    const resData: any = res?.data.value
    if (resData.success) {
      if (resData.data) {
        dataCategory.value = resData.data || 0
      }
    }
    return null
  } catch (e: any) {}
}

onMounted(() => {
  getDataCategoryVehicle()
  const id: string = route.query.id ? String(route.query.id) : ''

  const tollStationExpired: string = route.query['toll-station-expired'] ? String(route.query['toll-station-expired']) : ''
  if (id || tollStationExpired) {
    getInfoVehicleEdit(id || tollStationExpired)
    checkDisabledEdit.value = false
  }
  const idViewDetail: string = route.query.idViewDetail ? String(route.query.idViewDetail) : '' // id xem chi tiết
  const action: string = route.query.action ? String(route.query.action) : '' // id action
  if (idViewDetail && action === 'view') {
    vehicleId.value = idViewDetail
    if (showDrawerListVehicleDetail.value) {
      showDrawerListVehicleDetail.value.openEditVehicle(idViewDetail)
    }
  }
})

watch(
  () => route.query,
  () => {
    const id: string = route.query.id ? String(route.query.id) : ''

    const tollStationExpired: string = route.query['toll-station-expired'] ? String(route.query['toll-station-expired']) : ''
    if (id || tollStationExpired) {
      getInfoVehicleEdit(id || tollStationExpired)
      checkDisabledEdit.value = false
    }
  }
)

const getNameTypeVehicle = (vehicleLoad: number | undefined | null) => {
  if (typeof vehicleLoad === 'number') {
    let findCategory: any = null
    findCategory = dataCategory.value.find((item: { [key: string]: any }) => {
      if ((item.from = vehicleLoad && item.to >= vehicleLoad)) {
        return true
      }
      return false
    })
    if (!findCategory) {
      findCategory = dataCategory.value.find((item: { [key: string]: any }) => {
        if (item.from < vehicleLoad && $lodash.isNil(item.to)) {
          return true
        }
        return false
      })
    }
    return findCategory ? findCategory.name || '' : ''
  }
  return ''
}

const handleCloseFromEdit = () => {
  useRouter().push({ name: 'trucker-id-vehicle-list', query: {} })
}
</script>
